import { ComponentPropsWithRef, forwardRef, ReactNode } from "react";

import { useTranslationContext } from "context/TranslationContext";
import cn from "lib/cn";

import { TButtonVariant } from "./types";

export interface ButtonProps extends ComponentPropsWithRef<"button"> {
  isLoading?: boolean;
  variant?: TButtonVariant;
  color?: "primary" | "lightGray" | "red" | "mustard" | "gray";
  size?: "sm" | "base" | "md";
  noHover?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  border?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      id,
      children,
      className,
      disabled: buttonDisabled,
      isLoading,
      variant = "contained",
      color = "primary",
      size = "base",
      leftIcon,
      rightIcon,
      border,
      noHover,
      ...rest
    },
    ref
  ) => {
    const { t } = useTranslationContext();
    const disabled = isLoading || buttonDisabled;
    return (
      <button
        id={id}
        data-testid={id}
        ref={ref}
        type="button"
        disabled={disabled}
        className={cn(
          "inline-flex items-center rounded-md font-normal justify-center",
          "focus-visible:ring-primary-500 focus:outline-none focus-visible:ring",
          "shadow-sm",
          "transition-colors duration-75 text-base",
          [
            BUTTON_STYLE[size],
            BUTTON_STYLE[`${variant}-${color}`],
            noHover && [BUTTON_STYLE[`noHover-${variant}-${color}`]],
          ],
          "disabled:cursor-not-allowed",
          isLoading &&
            "relative text-transparent transition-none hover:text-transparent disabled:cursor-wait",
          className
        )}
        {...rest}
        style={{
          border: `${border && "none"}`,
        }}
      >
        {leftIcon && (
          <div
            data-testid={id ? `leftIcon-${id}` : undefined}
            className={cn(["pr-2"])}
          >
            {leftIcon}
          </div>
        )}
        {isLoading ? t("loading") : children}
        {rightIcon && (
          <div
            data-testid={id ? `rightIcon-${id}` : undefined}
            className={cn(["pl-2"])}
          >
            {rightIcon}
          </div>
        )}
      </button>
    );
  }
);

export default Button;

export const BUTTON_STYLE: Record<string, string> = {
  "contained-primary":
    "bg-primary-500 text-white border border-primary-500 hover:bg-primary-600 hover:text-white disabled:text-white disabled:bg-primary-200 disabled:border-primary-200",
  "outlined-primary":
    "bg-white text-night border border-primary-500 hover:bg-primary-500 hover:text-white disabled:bg-primary-200 disabled:text-white disabled:border-primary-200",
  "contained-red":
    "bg-red-500 text-white border border-red-500 hover:bg-red-600 hover:text-white disabled:text-white disabled:bg-red-300 disabled:border-red-300",
  "outlined-red":
    "bg-white text-black border border-red-500 hover:bg-gray-100 hover:text-black disabled:bg-white disabled:border-red-200 disabled:text-gray-900",
  "contained-mustard":
    "bg-mustard-600 text-white border border-mustard-600 hover:bg-mustard-700 hover:text-white disabled:text-gray-900 disabled:bg-mustard-200 disabled:border-mustard-200",
  "outlined-mustard":
    "bg-white text-black border border-mustard-600 hover:bg-gray-100 hover:text-black disabled:bg-white disabled:border-mustard-200 disabled:text-gray-900",
  "contained-gray":
    "bg-gray-100 shadow-none text-night border border-gray-100 hover:bg-gray-200 hover:text-night disabled:border-white disabled:bg-white disabled:text-gray-900",

  // Need to fix the color of these styles below
  "contained-sky":
    "bg-sky-600 text-white border border-sky-600 hover:bg-sky-600 hover:text-white disabled:bg-gray-500",
  "outlined-sky":
    "bg-white text-sky-600 border border-sky-600 hover:bg-sky-600 hover:text-white disabled:bg-gray-500 disabled:text-white disabled:border-none",
  "outlined-noBorder":
    "bg-slate-100 text-black text-xs disabled:bg-gray-500 disabled:text-white disabled:border-none",
  "contained-lightGray":
    "bg-neutral-gray-600 shadow-none text-black border border-neutral-gray-600 hover:bg-neutral-gray-600 hover:text-black disabled:bg-lightGray-200 disabled:text-lightBlack-200",
  "outlined-lightGray":
    "bg-white text-black shadow-none  border border-neutral-gray-600 hover:bg-neutral-gray-600 hover:text-black disabled:bg-gray-500 disabled:text-white disabled:border-none",
  "noHover-outlined-primary":
    "bg-white text-primary-500 border border-primary-500 disabled:bg-gray-500 disabled:text-white disabled:border-none",
  "noHover-contained-red":
    "hover:bg-red-400 bg-red-400 text-white hover:text-white",
  "noHover-outlined-red": "hover:bg-white hover:text-red-400",

  md: "px-3.5 py-2 text-base",
  base: "px-3 py-1.5 text-sm md:text-base",
  sm: "px-2.5 py-1 text-xs md:text-sm",
};
